/// <amd-module name="Favorites/Panel/FavoritesUserSettingsService" />
import { IObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IVssObservableService, IVssPageContext, Services, VssObservableService } from "VSS/Platform/Context";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";

export interface FavoritesUserSettings {
    pluralName: string;
    order: number;
}

/**
 * Service for managing favorite user settings.
 */
export interface IFavoritesUserSettingsService extends IVssObservableService<FavoritesUserSettings[]> {
    /**
     * Get the users preferred favorite order
     */
    getFavoriteOrder: () => IFavoriteSettingsCollection;

    /**
     * Set the users preferred favorite order
     */
    setFavoriteOrderForArtifact: (settings: FavoritesUserSettings[]) => void;
}

export interface IFavoriteSettingsCollection extends IObservableValue<FavoritesUserSettings[] | undefined> {}

class FavoriteSettingsCollection extends ObservableValue<FavoritesUserSettings[] | undefined> implements IFavoriteSettingsCollection {
    constructor(private pageContext: IVssPageContext, favoriteSettings: FavoritesUserSettings[]) {
        super(favoriteSettings);

        const service = this.pageContext.getService<FavoritesUserSettingsService>("IFavoritesUserSettingsService");
        service.subscribe(this.onFavoriteOrderUpdated, "favoriteOrderUpdated");
    }

    private onFavoriteOrderUpdated = (settings: FavoritesUserSettings[]): void => {
        const val = this.value || [];

        if (val) {
            settings.forEach(setting => {
                const found = val.find(v => v.pluralName === setting.pluralName);
                if (found) {
                    found.order = setting.order;
                }
            });

            this.notify(settings, "collectionUpdated");
        }
    };
}

class FavoritesUserSettingsService extends VssObservableService<FavoritesUserSettings[]> implements IFavoritesUserSettingsService {
    private prefs: { [key: string]: any };

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);

        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        this.prefs = settingsService.getEntry<{ [key: string]: any }>("favorites/ordering", SettingsUserScope.Me) || {};
    }

    public getFavoriteOrder(): IFavoriteSettingsCollection {
        const list: FavoritesUserSettings[] = [];
        Object.keys(this.prefs).forEach(key => {
            list.push({
                pluralName: key,
                order: +this.prefs[key]
            });
        });

        return new FavoriteSettingsCollection(this.pageContext, list);
    }

    public setFavoriteOrderForArtifact(settings: FavoritesUserSettings[]): void {
        const settingsService = this.pageContext.getService<ISettingsService>("ISettingsService");
        const values: { [key: string]: any } = {};
        settings.forEach(setting => {
            values[`favorites/ordering/${setting.pluralName}`] = setting.order;
            this.prefs[setting.pluralName] = setting.order;
        });

        this._notify(settings, "favoriteOrderUpdated");
        settingsService.setEntries(values, SettingsUserScope.Me);
    }
}

Services.add("IFavoritesUserSettingsService", { serviceFactory: FavoritesUserSettingsService });
